import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StatusChip from "../common/StatusChip";
import { getArtifactPhaseLabel } from "../common/artifactPhases";

const sectionSx = {
  backgroundColor: "#fff",
  borderRadius: 1,
  border: 1,
  borderColor: "divider",
  p: 1.5,
  mb: 1.5,
};

const labelSx = {
  fontSize: 11,
  fontWeight: 600,
  color: "text.secondary",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  display: "block",
  mb: 0.5,
};

const monoSx = {
  fontFamily: "monospace",
  fontSize: 12,
  wordBreak: "break-all",
  lineHeight: 1.45,
};

const CUSTOMER_ACTION_LABELS = {
  approve: "Approve dry-run",
  provide_inputs: "Provide inputs",
  wait: "Wait for worker",
};

const formatCustomerAction = (action) =>
  CUSTOMER_ACTION_LABELS[action] || String(action || "—").replace(/_/g, " ");

const formatCellValue = (value) => {
  if (value == null || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

/** Human-readable label from API/snake/camel keys. */
const formatFieldLabel = (key) =>
  String(key)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const StringList = ({ items, empty = "None" }) => {
  if (!Array.isArray(items) || items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
        {empty}
      </Typography>
    );
  }
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.25, fontSize: 13, lineHeight: 1.5 }}>
      {items.map((item, i) => (
        <li key={i}>{typeof item === "object" ? JSON.stringify(item) : String(item)}</li>
      ))}
    </Box>
  );
};

const KeyValueRows = ({ rows }) => {
  const visible = rows.filter((r) => r.value != null && r.value !== "");
  if (!visible.length) return null;
  return (
    <Stack spacing={1}>
      {visible.map(({ label, value, mono }) => (
        <Box key={label}>
          <Typography sx={labelSx}>{label}</Typography>
          <Typography sx={mono ? monoSx : { fontSize: 13, lineHeight: 1.45 }}>{value}</Typography>
        </Box>
      ))}
    </Stack>
  );
};

const PermissionChips = ({ permissions }) => {
  if (!Array.isArray(permissions) || permissions.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
      {permissions.map((p) => (
        <Chip
          key={p}
          size="small"
          label={p}
          sx={{ fontFamily: "monospace", fontSize: 11, backgroundColor: "grey.100" }}
        />
      ))}
    </Box>
  );
};

const RawJsonAccordion = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <Accordion
      expanded={expanded}
      onChange={(_e, open) => setExpanded(open)}
      disableGutters
      elevation={0}
      sx={{
        backgroundColor: "transparent",
        "&:before": { display: "none" },
        border: 1,
        borderColor: "divider",
        borderRadius: "8px !important",
        overflow: "hidden",
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, px: 1.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          View raw JSON
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0, borderTop: 1, borderColor: "divider" }}>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.25,
            fontSize: 11,
            lineHeight: 1.45,
            maxHeight: 280,
            overflow: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            backgroundColor: "grey.50",
          }}
        >
          {JSON.stringify(data, null, 2)}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

/** Preview — pre: dry-run outcome captured by the worker. */
const PreviewPreView = ({ data }) => {
  const {
    check_id,
    resource_arn,
    playbook = {},
    preview_summary = {},
    side_effects = [],
    simulator = {},
    next_state,
    next_action_for_customer,
    schema_version,
  } = data;

  const plannedAction =
    preview_summary.action || playbook.action || "Planned remediation change";
  const simulatorOk =
    simulator.evaluated === true &&
    (!Array.isArray(simulator.denied) || simulator.denied.length === 0);

  return (
    <Box>
      <Alert severity="info" icon={false} sx={{ mb: 1.5, py: 1.25 }}>
        <Typography variant="caption" sx={labelSx}>
          Planned change
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.4 }}>
          {plannedAction}
        </Typography>
      </Alert>

      <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: "wrap", gap: 1 }}>
        {next_state && <StatusChip status={next_state} withTooltip={false} />}
        {next_action_for_customer && (
          <Chip
            size="small"
            label={`Next step: ${formatCustomerAction(next_action_for_customer)}`}
            sx={{ backgroundColor: "grey.100", fontWeight: 600 }}
          />
        )}
        {schema_version && (
          <Chip size="small" label={`Schema v${schema_version}`} sx={{ backgroundColor: "grey.100" }} />
        )}
      </Stack>

      <Box sx={sectionSx}>
        <Typography sx={{ ...labelSx, mb: 1 }}>Target</Typography>
        <KeyValueRows
          rows={[
            { label: "Check", value: check_id },
            { label: "Resource", value: resource_arn, mono: true },
          ]}
        />
      </Box>

      {Object.keys(preview_summary).length > 0 && (
        <Box sx={sectionSx}>
          <Typography sx={{ ...labelSx, mb: 1 }}>Preview details</Typography>
          <KeyValueRows
            rows={Object.entries(preview_summary)
              .filter(([k]) => k !== "action")
              .map(([k, v]) => ({
                label: formatFieldLabel(k),
                value: formatCellValue(v),
                mono: k.includes("arn") || k.includes("id"),
              }))}
          />
        </Box>
      )}

      <Box sx={sectionSx}>
        <Typography sx={{ ...labelSx, mb: 1 }}>IAM policy simulation</Typography>
        {simulator.evaluated == null ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
            Not evaluated.
          </Typography>
        ) : simulatorOk ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckCircleOutlineIcon color="success" fontSize="small" />
            <Typography variant="body2" sx={{ fontSize: 13 }}>
              All required actions are allowed for this role.
            </Typography>
          </Stack>
        ) : (
          <Alert severity="warning" sx={{ py: 0.75 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <ErrorOutlineIcon fontSize="small" sx={{ mt: 0.25 }} />
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
                  Denied actions
                </Typography>
                <StringList items={simulator.denied} empty="Unknown denial" />
              </Box>
            </Stack>
          </Alert>
        )}
      </Box>

      {(playbook.required_actions?.length > 0 ||
        playbook.consequences?.length > 0 ||
        side_effects?.length > 0) && (
        <Box sx={sectionSx}>
          <Typography sx={{ ...labelSx, mb: 1 }}>Playbook</Typography>
          {playbook.action && preview_summary.action !== playbook.action && (
            <Box sx={{ mb: 1 }}>
              <Typography sx={labelSx}>Action</Typography>
              <Typography sx={{ fontSize: 13 }}>{playbook.action}</Typography>
            </Box>
          )}
          {playbook.required_actions?.length > 0 && (
            <Box sx={{ mb: playbook.consequences?.length || side_effects?.length ? 1.25 : 0 }}>
              <Typography sx={labelSx}>Required IAM permissions</Typography>
              <PermissionChips permissions={playbook.required_actions} />
            </Box>
          )}
          {playbook.consequences?.length > 0 && (
            <Box sx={{ mb: side_effects?.length ? 1.25 : 0 }}>
              <Typography sx={labelSx}>Consequences</Typography>
              <StringList items={playbook.consequences} />
            </Box>
          )}
          {side_effects?.length > 0 && (
            <Box>
              <Typography sx={labelSx}>Side effects</Typography>
              <StringList items={side_effects} />
            </Box>
          )}
        </Box>
      )}

      <RawJsonAccordion data={data} />
    </Box>
  );
};

/** Preview — playbook: playbook definition snapshot. */
const PlaybookArtifactView = ({ data }) => {
  const playbook = data?.playbook || data;
  const {
    action,
    consequences = [],
    required_actions = [],
    required_inputs = [],
    required_resources = [],
    side_effects = [],
    ...rest
  } = playbook;

  return (
    <Box>
      {action && (
        <Alert severity="info" icon={false} sx={{ mb: 1.5, py: 1.25 }}>
          <Typography variant="caption" sx={labelSx}>
            Playbook action
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 14 }}>
            {action}
          </Typography>
        </Alert>
      )}

      {required_actions.length > 0 && (
        <Box sx={sectionSx}>
          <Typography sx={{ ...labelSx, mb: 1 }}>Required IAM permissions</Typography>
          <PermissionChips permissions={required_actions} />
        </Box>
      )}

      {required_inputs.length > 0 && (
        <Box sx={sectionSx}>
          <Typography sx={{ ...labelSx, mb: 1 }}>Required inputs</Typography>
          <StringList items={required_inputs} />
        </Box>
      )}

      {required_resources.length > 0 && (
        <Box sx={sectionSx}>
          <Typography sx={{ ...labelSx, mb: 1 }}>Required resources</Typography>
          <StringList items={required_resources} />
        </Box>
      )}

      {consequences.length > 0 && (
        <Box sx={sectionSx}>
          <Typography sx={{ ...labelSx, mb: 1 }}>Consequences</Typography>
          <StringList items={consequences} />
        </Box>
      )}

      {side_effects.length > 0 && (
        <Box sx={sectionSx}>
          <Typography sx={{ ...labelSx, mb: 1 }}>Side effects</Typography>
          <StringList items={side_effects} />
        </Box>
      )}

      {Object.keys(rest).length > 0 && (
        <Box sx={sectionSx}>
          <Typography sx={{ ...labelSx, mb: 1 }}>Additional fields</Typography>
          <KeyValueRows
            rows={Object.entries(rest).map(([k, v]) => ({
              label: k.replace(/_/g, " "),
              value: formatCellValue(v),
            }))}
          />
        </Box>
      )}

      <RawJsonAccordion data={data} />
    </Box>
  );
};

/** Execution — AWS responses: API call results from remediation. */
const AwsResponsesView = ({ data }) => {
  const entries = Array.isArray(data)
    ? data
    : Array.isArray(data?.responses)
      ? data.responses
      : Array.isArray(data?.calls)
        ? data.calls
        : null;

  if (entries?.length) {
    const columns = [...new Set(entries.flatMap((row) => Object.keys(row || {})))].slice(0, 6);
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, mb: 1.5 }}>
          {entries.length} AWS API {entries.length === 1 ? "call" : "calls"} recorded during execution.
        </Typography>
        <TableContainer sx={{ ...sectionSx, p: 0, overflow: "auto", mb: 1.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                {columns.map((col) => (
                  <TableCell key={col} sx={{ fontWeight: 700, fontSize: 11 }}>
                    {formatFieldLabel(col)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((row, i) => (
                <TableRow key={i}>
                  {columns.map((col) => (
                    <TableCell key={col} sx={{ fontFamily: "monospace", fontSize: 11, maxWidth: 200 }}>
                      {formatCellValue(row?.[col])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <RawJsonAccordion data={data} />
      </Box>
    );
  }

  if (data && typeof data === "object" && !Array.isArray(data)) {
    const scalarRows = Object.entries(data)
      .filter(([, v]) => v == null || typeof v !== "object")
      .map(([k, v]) => ({
        label: formatFieldLabel(k),
        value: formatCellValue(v),
        mono: k.includes("arn") || k.endsWith("_id"),
      }));

    return (
      <Box>
        {scalarRows.length > 0 && (
          <Box sx={sectionSx}>
            <Typography sx={{ ...labelSx, mb: 1.25 }}>Response</Typography>
            <KeyValueRows rows={scalarRows} />
          </Box>
        )}

        <RawJsonAccordion data={data} />
      </Box>
    );
  }

  return <StructuredFallbackView data={data} />;
};

/** Execution — pre/post: checkpoint payloads. */
const ExecutionPhaseView = ({ data, phase }) => {
  const status = data?.status || data?.result;
  const message = data?.message || data?.detail || data?.summary;

  return (
    <Box>
      {(status || message) && (
        <Box sx={sectionSx}>
          <Typography sx={{ ...labelSx, mb: 1 }}>{getArtifactPhaseLabel(phase)}</Typography>
          {status && (
            <Chip
              size="small"
              label={String(status)}
              sx={{ mb: message ? 1 : 0, fontWeight: 600, backgroundColor: "grey.100" }}
            />
          )}
          {message && <Typography sx={{ fontSize: 13, lineHeight: 1.45 }}>{message}</Typography>}
        </Box>
      )}
      <StructuredFallbackView data={data} hideRawIfEmpty />
    </Box>
  );
};

/** Generic structured view for unknown shapes — groups top-level keys into sections. */
const StructuredFallbackView = ({ data, hideRawIfEmpty = false }) => {
  if (data == null) return null;

  if (typeof data !== "object" || Array.isArray(data)) {
    return (
      <Box>
        <Box sx={sectionSx}>
          <Typography sx={monoSx}>{formatCellValue(data)}</Typography>
        </Box>
        <RawJsonAccordion data={data} />
      </Box>
    );
  }

  const entries = Object.entries(data);
  if (!entries.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
        Artifact is empty.
      </Typography>
    );
  }

  const scalarEntries = entries.filter(([, v]) => v == null || typeof v !== "object");
  const objectEntries = entries.filter(([, v]) => v != null && typeof v === "object" && !Array.isArray(v));
  const arrayEntries = entries.filter(([, v]) => Array.isArray(v));

  return (
    <Box>
      {scalarEntries.length > 0 && (
        <Box sx={sectionSx}>
          <Typography sx={{ ...labelSx, mb: 1 }}>Summary</Typography>
          <KeyValueRows
            rows={scalarEntries.map(([k, v]) => ({
              label: formatFieldLabel(k),
              value: formatCellValue(v),
              mono: k.includes("arn") || k.endsWith("_id"),
            }))}
          />
        </Box>
      )}

      {objectEntries.map(([key, value]) => (
        <Box key={key} sx={sectionSx}>
          <Typography sx={{ ...labelSx, mb: 1 }}>{formatFieldLabel(key)}</Typography>
          <KeyValueRows
            rows={Object.entries(value).map(([k, v]) => ({
              label: formatFieldLabel(k),
              value: formatCellValue(v),
              mono: typeof v === "string" && (k.includes("arn") || v.startsWith("arn:")),
            }))}
          />
        </Box>
      ))}

      {arrayEntries.map(([key, value]) => (
        <Box key={key} sx={sectionSx}>
          <Typography sx={{ ...labelSx, mb: 1 }}>
            {formatFieldLabel(key)} ({value.length})
          </Typography>
          {value.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
              None
            </Typography>
          ) : typeof value[0] === "object" ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "grey.50" }}>
                    {[...new Set(value.flatMap((row) => Object.keys(row || {})))].slice(0, 5).map((col) => (
                      <TableCell key={col} sx={{ fontWeight: 700, fontSize: 11 }}>
                        {col.replace(/_/g, " ")}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {value.map((row, i) => (
                    <TableRow key={i}>
                      {[...new Set(value.flatMap((r) => Object.keys(r || {})))].slice(0, 5).map((col) => (
                        <TableCell key={col} sx={{ fontSize: 11, fontFamily: "monospace" }}>
                          {formatCellValue(row?.[col])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <StringList items={value} />
          )}
        </Box>
      ))}

      {!(hideRawIfEmpty && entries.length === 0) && <RawJsonAccordion data={data} />}
    </Box>
  );
};

const looksLikePreviewPre = (data) =>
  data &&
  typeof data === "object" &&
  !Array.isArray(data) &&
  (data.preview_summary != null || data.next_state != null) &&
  data.check_id != null;

const ArtifactContentView = ({ phase, data }) => {
  if (data == null) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
        Artifact is empty.
      </Typography>
    );
  }

  if (phase === "preview/pre" || (phase !== "preview/playbook" && looksLikePreviewPre(data))) {
    return <PreviewPreView data={data} />;
  }
  if (phase === "preview/playbook") {
    return <PlaybookArtifactView data={data} />;
  }
  if (phase === "exec/aws_responses") {
    return <AwsResponsesView data={data} />;
  }
  if (phase === "exec/pre" || phase === "exec/post") {
    return <ExecutionPhaseView data={data} phase={phase} />;
  }

  return <StructuredFallbackView data={data} />;
};

export default ArtifactContentView;
